import { ProviderClaimPayment } from '../../api/server/models/ProviderClaimPayment';
import { ProviderClearinghouseEraItem } from '../../api/server/models/ProviderClearinghouseEraItem';
import { amountOf, IProviderAdjustment } from './remit-adjustments';
import { claimStatus, shortGroup } from './remit-codes';

/**
 * One remit's claims and their service lines, as the expanded remit shows them (FE-163, crosswalk §2). The ERA and
 * the EOB arrive in different shapes; both become `IRemitClaim`, so the page draws one thing.
 *
 * ## Derived columns — from the crosswalk, and only those
 *
 * - **Allowed** (ERA): billed less the CO, OA and PI adjustments. An EOB line carries `allowedAmount` itself.
 * - **Deductible**: the PR-1 adjustments. **Coinsurance**: the PR-2 adjustments. PR-3 (co-pay) is in neither column —
 *   the crosswalk names codes 1 and 2 only; it still shows in the line's adjustment list.
 *
 * Not built, because the crosswalk does not source them (Vadim, 2026-10-08: not in the crosswalk → not built): the
 * mockup's PT RESP, ADJ TO TOTAL PREV PD, INTEREST, LATE FILING CHARGE and NET lines under each claim.
 */

export interface IRemitAdjustment {
    /** `CO`, `PR`… */
    group: string;
    code: string;
    amount: number;
}

export interface IRemitLine {
    key: string;
    fromDos?: string;
    throughDos?: string;
    /** Place of service. EOB only — an 835 does not carry it. */
    placeOfService?: string;
    units?: number;
    procedureCode?: string;
    modifiers: string[];
    billed: number;
    allowed: number;
    deductible: number;
    coinsurance: number;
    paid: number;
    adjustments: IRemitAdjustment[];
}

export interface IRemitClaim {
    key: string;
    patient?: string;
    subscriber?: string;
    subscriberId?: string;
    pcn?: string;
    icn?: string;
    groupId?: string;
    contract?: string;
    /** CLP02 when the status has one, and its words. */
    status: { code?: string; label: string };
    crossoverCarrier?: string;
    /** The claim's NPI — the 835 reports a line-level rendering NPI only in its REF*HPI, which the API does not parse. */
    renderingNpi?: string;
    /** Linked to a claim in ENTER: an ERA item with a payment, an EOB payment with a claim record. */
    posted: boolean;
    /** The payment to resolve the claim from, for View Claim. */
    paymentId?: string;
    /** The ERA item's id, for its claim PDF. ERA only. */
    itemId?: string;
    /** What the payer paid on this claim: CLP04 (ERA item `paidAmount`) / the EOB payment's `payerAmount`. */
    paid: number;
    lines: IRemitLine[];
    totals: Omit<IRemitLine, 'key' | 'modifiers' | 'adjustments' | 'fromDos' | 'throughDos' | 'placeOfService' | 'units' | 'procedureCode'> & {
        adjusted: number;
    };
}

const cents = (value: number): number => Math.round(value * 100) / 100;
const sum = (values: number[]): number => cents(values.reduce((total, value) => total + value, 0));
const name = (first?: string, last?: string): string | undefined =>
    [last, first].map(part => (part || '').trim()).filter(Boolean).join(', ') || undefined;

const sumWhere = (adjustments: IRemitAdjustment[], test: (adjustment: IRemitAdjustment) => boolean): number =>
    sum(adjustments.filter(test).map(adjustment => adjustment.amount));
const isPr = (code: string) => (adjustment: IRemitAdjustment): boolean =>
    adjustment.group === 'PR' && adjustment.code === code;

/**
 * The adjustments that stand between the charge and the allowed amount (crosswalk §2): CO, OA and PI. Except CARC
 * 253, sequestration — it is taken from the PAYMENT after the allowed amount is set (CMS prints AMT*B6 before it), and it
 * sits on nearly every Medicare line, so counting it would understate allowed on exactly the remits billers check most.
 */
const reducesAllowed = (adjustment: IRemitAdjustment): boolean =>
    ['CO', 'OA', 'PI'].includes(adjustment.group) && adjustment.code !== '253';

const totalsOf = (lines: IRemitLine[]): IRemitClaim['totals'] => ({
    billed: sum(lines.map(line => line.billed)),
    allowed: sum(lines.map(line => line.allowed)),
    deductible: sum(lines.map(line => line.deductible)),
    coinsurance: sum(lines.map(line => line.coinsurance)),
    paid: sum(lines.map(line => line.paid)),
    adjusted: sum(lines.flatMap(line => line.adjustments.map(adjustment => adjustment.amount))),
});

export const eraClaim = (item: ProviderClearinghouseEraItem, index = 0): IRemitClaim => {
    const lines = (item.charges || []).map(
        (charge, line): IRemitLine => {
            const adjustments = (charge.adjustments || []).map(adjustment => ({
                group: shortGroup(adjustment.group),
                code: adjustment.code || '',
                amount: amountOf(adjustment.amount),
            }));
            const billed = amountOf(charge.chargeAmount);
            return {
                key: `${item.id || index}:${line}`,
                fromDos: charge.fromDos,
                throughDos: charge.throughDos,
                units: charge.units,
                procedureCode: charge.procedureCode,
                modifiers: charge.modifiers || [],
                billed,
                allowed: cents(billed - sumWhere(adjustments, reducesAllowed)),
                deductible: sumWhere(adjustments, isPr('1')),
                coinsurance: sumWhere(adjustments, isPr('2')),
                paid: amountOf(charge.paidAmount),
                adjustments,
            };
        },
    );
    return {
        key: item.id || String(index),
        patient: name(item.patientFirst, item.patientLast),
        subscriber: name(item.subscriberFirst, item.subscriberLast),
        subscriberId: item.subscriberId,
        pcn: item.pcn,
        icn: item.icn,
        groupId: item.groupId,
        contract: item.contract,
        status: claimStatus(item.statusCode),
        crossoverCarrier: item.crossoverCarrier,
        renderingNpi: item.npi,
        posted: (item.paymentIds || []).length > 0,
        paymentId: (item.paymentIds || [])[0],
        itemId: item.id,
        paid: amountOf(item.paidAmount),
        lines,
        totals: totalsOf(lines),
    };
};

/**
 * An EOB payment. It names no patient — the crosswalk sources that from the linked claim — and its claim is found
 * through the payment, never from `professionalId`, which is the claim's professional SUB-record (measured
 * 2026-10-01, FE-116: every by-id claim endpoint answers 500 for it).
 */
export const eobClaim = (payment: ProviderClaimPayment, index = 0): IRemitClaim => {
    const lines = (payment.items || []).map(
        (item, line): IRemitLine => {
            const adjustments = (item.adjustments || []).map(adjustment => ({
                group: shortGroup(adjustment.group),
                code: adjustment.reasonCode || '',
                amount: amountOf(adjustment.amount),
            }));
            return {
                key: `${payment.id || index}:${line}`,
                fromDos: item.fromDos,
                throughDos: item.throughDos,
                placeOfService: item.placeOfServiceCode,
                units: item.paidCount ?? item.chargeCount,
                procedureCode: item.procedureCode,
                modifiers: item.modifierCodes || [],
                billed: amountOf(item.chargeAmount),
                allowed: amountOf(item.allowedAmount),
                deductible: sumWhere(adjustments, isPr('1')),
                coinsurance: sumWhere(adjustments, isPr('2')),
                paid: amountOf(item.paidAmount),
                adjustments,
            };
        },
    );
    return {
        key: payment.id || String(index),
        icn: payment.payerControlNumber,
        status: claimStatus(payment.status),
        posted: Boolean(payment.professionalId || payment.institutionalId || payment.dentalId || payment.nynf3Id),
        paymentId: payment.id,
        paid: amountOf(payment.payerAmount),
        lines,
        totals: totalsOf(lines),
    };
};

/**
 * The balance line under the provider adjustments: what the claims paid (each claim's own paid amount, as the
 * crosswalk sums it), what the provider adjustments changed, and the remit's paid amount (BPR02). `difference` is
 * what the three do not explain.
 *
 * Measured on production 2026-10-08: 60 of 60 ERAs balance to the cent, 23 of them with PLB adjustments. Of 40
 * EOBs, 35 balance; the other 5 have NO claim payments attached yet, so their whole check is unexplained — a real
 * state the page must say in words, not hide behind a balance line.
 */
export const remitBalance = (
    claims: IRemitClaim[],
    providerAdjustments: IProviderAdjustment[],
    paid: number,
): { claimRemits: number; providerAdjustments: number; paid: number; difference: number } => {
    const claimRemits = sum(claims.map(claim => claim.paid));
    const adjusted = sum(providerAdjustments.map(adjustment => adjustment.effect));
    return { claimRemits, providerAdjustments: adjusted, paid, difference: cents(paid - (claimRemits + adjusted)) };
};

/** `2 / 4 posted` — the crosswalk's posted count. */
export const postedCount = (claims: IRemitClaim[]): { posted: number; total: number } => ({
    posted: claims.filter(claim => claim.posted).length,
    total: claims.length,
});
